// Reglas de alerta UNE 23500 para la curva caudal/presión de bombas.
// Se ejecutan sobre PruebaBombaData y devuelven mensajes para la UI y el PDF.

import type { PruebaBombaData } from '@/components/checklist/PruebaPrestacionesBlock';
import { presionToCanonical } from '@/lib/checklists/units';

export type NivelAlerta = 'info' | 'warning' | 'anomalia';
export type DestinoPDF = 'anomalia' | 'alerta' | 'ninguno';

export interface AlertaPrueba {
  id: string;
  nivel: NivelAlerta;
  icono: string;
  mensaje: string;
  destinoPDF: DestinoPDF;
  /** % de la fila a la que aplica (para resaltar en la tabla). */
  pct?: number;
}

const findPunto = (data: PruebaBombaData, pct: number) =>
  data.puntos.find(p => p.pct === pct);

/** Presión nominal de placa en bar (unidad canónica). */
const nominalBar = (data: PruebaBombaData): number =>
  presionToCanonical(Number(data.nominal.presion) || 0, data.unidades.placa.presion);

/** Presión medida en bar (unidad canónica). */
const puntoBar = (data: PruebaBombaData, pct: number): number => {
  const p = findPunto(data, pct);
  if (!p) return 0;
  return presionToCanonical(Number(p.presion) || 0, data.unidades.instrumento.presion);
};

export function evaluarAlertasBomba(data: PruebaBombaData): AlertaPrueba[] {
  const out: AlertaPrueba[] = [];

  // 1. Aviso operativo fijo en 0% (siempre visible).
  out.push({
    id: 'op-0pct',
    nivel: 'info',
    icono: '⚠️',
    mensaje:
      'Operativa: no mantener la bomba a caudal cero más de 2 min (riesgo de sobrecalentamiento y daños en cierres).',
    destinoPDF: 'ninguno',
    pct: 0,
  });

  const nomBar = nominalBar(data);
  const p0 = puntoBar(data, 0);
  const p100 = puntoBar(data, 100);
  const p140 = puntoBar(data, 140);

  // 2. Sobrepresión en cierre (> 130 % de placa).
  if (nomBar > 0 && p0 > 0 && p0 > 1.3 * nomBar) {
    out.push({
      id: 'une-sobrepresion',
      nivel: 'anomalia',
      icono: '🚨',
      mensaje: 'Defecto UNE 23500: riesgo de sobrepresión en red (supera 130 % de placa).',
      destinoPDF: 'anomalia',
      pct: 0,
    });
  }

  // 3. Rendimiento insuficiente a régimen nominal (P(100%) < placa).
  if (nomBar > 0 && p100 > 0 && p100 < nomBar) {
    out.push({
      id: 'une-rendimiento-100',
      nivel: 'anomalia',
      icono: '❌',
      mensaje: 'Defecto UNE 23500: rendimiento insuficiente a régimen nominal.',
      destinoPDF: 'anomalia',
      pct: 100,
    });
  }

  // 4. Rendimiento al 140 % < 70 % de placa.
  if (nomBar > 0 && p140 > 0 && p140 < 0.7 * nomBar) {
    out.push({
      id: 'une-rendimiento-140',
      nivel: 'anomalia',
      icono: '🚨',
      mensaje: 'Defecto UNE 23500: rendimiento insuficiente por debajo del 70 % mínimo.',
      destinoPDF: 'anomalia',
      pct: 140,
    });
  }

  // 5. Alerta instalación: P(140%) < 3 bar (equivalente).
  if (p140 > 0 && p140 < 3) {
    out.push({
      id: 'inst-3bar',
      nivel: 'warning',
      icono: '⚠️',
      mensaje:
        'Alerta de instalación: presión en sobrecarga inferior a 3 bar. Riesgo de presión insuficiente (< 2 bar dinámicos) en BIEs/rociadores por pérdidas de carga.',
      destinoPDF: 'alerta',
      pct: 140,
    });
  }

  return out;
}

export const contarAnomalias = (alertas: AlertaPrueba[]) =>
  alertas.filter(a => a.nivel === 'anomalia').length;
